import { styled } from 'dripsy';
import { observer } from 'mobx-react-lite';
import React, { useMemo } from 'react';
import { View } from 'react-native';
import { useTranslation } from 'react-i18next';

import {
  CommentInput,
  ICommentInputProps,
  IconTouchable,
  BaseText,
  Line,
  CommentInputModeEnum,
  CommentInputTargetType,
} from '@components';
import { CommentList } from '@models/Comments/CommentListModel';
import type { ICommentScreenStore } from '../CommentScreen';

const BarContainer = styled(View)({
  p: '$6',
});

const ReplyContainer = styled(View)({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
  bg: '$background40',
  px: '$6',
  py: '$5',
});

const ReplyUserFullName = styled(BaseText)({
  color: '$heading800',
  variant: 'text.small',
});

export interface ICommentInputFooterProps
  extends Pick<ICommentInputProps, 'inputRef'> {
  store: ICommentScreenStore;
  list: CommentList;
}

export const CommentInputFooter = observer<ICommentInputFooterProps>(
  ({ list, store, inputRef }) => {
    const { t } = useTranslation();

    const commentInputTarget = useMemo<CommentInputTargetType>(() => {
      if (store.comment) {
        if (store.isUpdateMode) {
          return {
            entity: store.comment,
            mode: CommentInputModeEnum.Update,
          };
        } else {
          return {
            entity: store.comment.comments as CommentList,
            mode: CommentInputModeEnum.Create,
          };
        }
      }

      return {
        entity: list,
        mode: CommentInputModeEnum.Create,
      };
    }, [store.comment, store.mode]);

    const status = store.isUpdateMode
      ? t('comment.editComment')
      : `${t('comment.replyTo')} ${store.comment?.user.fullName}`;

    return (
      <View>
        {!!store.comment && (
          <ReplyContainer>
            <ReplyUserFullName>{status}</ReplyUserFullName>
            <IconTouchable
              name="close-light"
              onPress={store.clearComment}
            />
          </ReplyContainer>
        )}
        <Line />
        <BarContainer>
          <CommentInput
            target={commentInputTarget}
            inputRef={inputRef}
            onSubmit={store.clearComment}
          />
        </BarContainer>
      </View>
    );
  },
);
